/**
 * How a client-sale row (`payment_transactions`) is labelled, shared by every screen that
 * lists one: the cross-tenant ledger (ClientTransactionsList) and a practice's Tenant 360
 * drill-down. One vocabulary, so both screens name a sale in the same words.
 * Both functions are pure and take the superadmin translator `t`.
 */

// Values must match the backend's CLIENT_TX_KINDS exactly, which is the writer vocabulary
// `modules/payments/schemas.PaymentTxKind` (every kind a checkout can store, "pack" included).
// "telehealth_prepaid" was dropped from both sides 2026-09-02 with the legacy prepaid
// telehealth lane — Flow C (per-minute) sales are ordinary "booking" rows.
export const KIND_OPTIONS = ["booking", "course", "pathway", "pack", "product", "program", "invoice", "giftcard"];
// Historical rows still carry the retired prepaid-telehealth kind; it is no longer a filter
// option (the backend enum dropped it) but a row can still show it.
export const LEGACY_TELEHEALTH_KIND = "telehealth_prepaid";
// The checkout statuses an unpaid row can be in, as written by the payments module:
// "initiated" (row inserted before the Stripe session exists), "open"/"expired" (Stripe's
// Checkout Session status, copied by `set_non_paid_status`), "failed". Anything else (e.g.
// Stripe's "complete" on a still-unpaid async payment) reads as plain "Unpaid".
const UNPAID_STATUSES = ["initiated", "open", "expired", "failed"];

/** Whether `kind` is one the kind label can name (the filter set plus the retired kind). */
export const isKnownKind = (kind) => KIND_OPTIONS.includes(kind) || kind === LEGACY_TELEHEALTH_KIND;

/**
 * The kind column's label: the SAME words the kind filter uses, so a row and the filter that
 * selects it never disagree. A value outside both the filter set and the one retired kind is
 * "Other" — the raw backend string is for the tooltip, never the cell.
 */
export function clientTxKindLabel(kind, t) {
  if (KIND_OPTIONS.includes(kind)) return t(`ctx.kind.${kind}`);
  if (kind === LEGACY_TELEHEALTH_KIND) return t("ctx.kind.legacyTelehealth");
  return t("ctx.kind.other");
}

/**
 * One status chip per row. The order below is deliberate and mirrors what the backend
 * actually tracks as the row's most urgent fact: an active refund or dispute always
 * outranks a stale "paid" read, and a recorded-but-unsettled refund obligation outranks
 * "paid" too — a practitioner owing money back is not a closed transaction. Only once
 * none of those apply does the plain payment_status decide. An unpaid row maps its checkout
 * `status` through a closed, translated set; a value outside it reads "Unpaid" with the raw
 * value in the tooltip, so an unforeseen status is neither hidden nor shown untranslated.
 *
 * `tx`: { refund_status, disputed_cents, refund_obligation_state, payment_status, status }.
 * Returns { label, cls, title? }.
 */
export function statusChip(tx, t) {
  if (tx.refund_status === "refunded") {
    return { label: t("ctx.status.refunded"), cls: "bg-rose-100 text-rose-700" };
  }
  if (tx.refund_status === "partial") {
    return { label: t("ctx.status.partial"), cls: "bg-rose-100 text-rose-700" };
  }
  // A refund sent to Stripe whose answer never came back (`payments.service`, unknown
  // outcome): the money may or may not have reached the client until it is reconciled.
  if (tx.refund_status === "unverified") {
    return { label: t("ctx.status.refundUnverified"), cls: "bg-amber-100 text-amber-700", title: t("ctx.status.refundUnverifiedHint") };
  }
  if (Number(tx.disputed_cents || 0) > 0) {
    return { label: t("ctx.status.disputed"), cls: "bg-purple-100 text-purple-700" };
  }
  if (tx.refund_obligation_state === "owed") {
    return { label: t("ctx.status.refundOwed"), cls: "bg-amber-100 text-amber-700" };
  }
  if (tx.payment_status === "paid") {
    return { label: t("ctx.status.paid"), cls: "bg-emerald-100 text-emerald-700" };
  }
  if (UNPAID_STATUSES.includes(tx.status)) {
    return { label: t(`ctx.status.${tx.status}`), cls: "bg-slate-100 text-slate-600" };
  }
  return { label: t("ctx.paymentStatus.unpaid"), cls: "bg-slate-100 text-slate-600", title: tx.status };
}
